import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Popup } from "devextreme-react";
import Form, { ButtonItem, FormRef, GroupItem, SimpleItem } from "devextreme-react/form";
import { ButtonType } from "devextreme/common";

import { Utilities } from "@/shared/utils/utilities";
import { FiscalPeriod, ProductionDeterminantHistory } from "@/shared/models/ProductionDeterminantListing";
import { ReviewProductionIndicesApi } from "@/shared/api/ReviewProductionIndices.api";
import { generatePostObjFormData, validateDeterminantIsDirty } from "@/shared/business/ReviewProductionIndices";
import { FieldKeyNames } from "@/shared/constants";

interface Props {
  visible: boolean;
  fiscalPeriod: FiscalPeriod;
  determinant: ProductionDeterminantHistory;
  closeForm: (reloadData: boolean) => Promise<void>;
}

const ReviewProductionIndicesEditForm = ({ visible, fiscalPeriod, determinant, closeForm }: Props) => {
  const formRef = useRef<FormRef>(null);

  const currentFY = useMemo(() => fiscalPeriod.fiscalYear, [fiscalPeriod.fiscalYear]);
  const hasCalculationFormula = useMemo(() => !Utilities.isNullOrEmptyString(determinant.calculationFormula), [determinant.calculationFormula]);

  const [formData, setFormData] = useState({} as any);

  useEffect(() => {
    if (!determinant || !determinant.determinantPeriods) {
      return;
    }

    initFormData(determinant);
  }, [determinant]);

  const handleSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();

      var postData = generatePostObjFormData(formData, determinant);
      if (await ReviewProductionIndicesApi.updateIndex(postData)) {
        closeForm && (await closeForm(true));
      }
    },
    [closeForm, formData, determinant]
  );

  const handleCancel = useCallback(async () => {
    if (validateDeterminantIsDirty(formData, determinant) && !window.confirm(`You have unsaved changes\r\nAre you sure you want to cancel?`)) {
      return;
    }

    // The form is always existed, so we need to reset the form data when it closed.
    initFormData(determinant);
    closeForm && (await closeForm(false));
  }, [formData, determinant, closeForm]);

  const initFormData = (determinant: ProductionDeterminantHistory) => {
    var formData = { Determinant: determinant.determinantDisplayAs, Source: determinant.determinantPlant } as any;
    determinant.determinantPeriods.forEach((period) => {
      formData[period.fiscalPeriod.fiscalYear + period.fiscalPeriod.calendarName] = period.value;
    });

    setFormData(formData);
  };

  return (
    <Popup
      width={550}
      height={"90%"}
      showTitle={true}
      title={"Update Production Index"}
      dragEnabled={true}
      hideOnOutsideClick={false}
      visible={visible}
      onHiding={async (e) => {
        e.cancel = true;
        await handleCancel();
      }}
      showCloseButton={true}
    >
      <form onSubmit={handleSubmit}>
        <Form ref={formRef} formData={formData} labelMode="floating" colCount={2} showColonAfterLabel={true}>
          <GroupItem colSpan={2}>
            <SimpleItem
              label={{ text: "Index Name" }}
              dataField={FieldKeyNames.Determinant}
              editorType="dxTextBox"
              editorOptions={{
                disabled: true,
              }}
            />
            {Utilities.isNullOrEmptyString(determinant.determinantPlant) ? null : (
              <SimpleItem
                label={{ text: "Plant" }}
                dataField={FieldKeyNames.Source}
                editorType="dxTextBox"
                editorOptions={{
                  disabled: true,
                }}
              />
            )}
          </GroupItem>

          <GroupItem colSpan={2}>
            {hasCalculationFormula ? <p>This is calculated by ({determinant.calculationFormula}) and is unable to be edited</p> : null}
          </GroupItem>

          <GroupItem cssClass="update-index-editor border border-1 pb-1 ps-1 me-2">
            <SimpleItem>
              <h3>FY {currentFY - 1}</h3>
            </SimpleItem>
            {determinant && determinant.determinantPeriods
              ? determinant.determinantPeriods
                  .filter((item) => item.fiscalPeriod.fiscalYear === currentFY - 1)
                  .map((period, index) => (
                    <SimpleItem
                      key={index}
                      label={{ text: period.fiscalPeriod.calendarName }}
                      dataField={period.fiscalPeriod.fiscalYear + period.fiscalPeriod.calendarName}
                      editorType="dxTextBox"
                      editorOptions={{
                        disabled: hasCalculationFormula,
                      }}
                    ></SimpleItem>
                  ))
              : null}
          </GroupItem>

          <GroupItem cssClass="update-index-editor border border-1 pb-1 pe-1">
            <SimpleItem>
              <h3>FY {currentFY}</h3>
            </SimpleItem>
            {determinant && determinant.determinantPeriods
              ? determinant.determinantPeriods
                  .filter((item) => item.fiscalPeriod.fiscalYear === currentFY)
                  .map((period, index) => (
                    <SimpleItem
                      key={index}
                      label={{ text: period.fiscalPeriod.calendarName }}
                      dataField={period.fiscalPeriod.fiscalYear + period.fiscalPeriod.calendarName}
                      editorType="dxTextBox"
                      editorOptions={{
                        disabled: hasCalculationFormula,
                        focusStateEnabled: true,
                        onInitialized: (e: any) => {
                          if (period.fiscalPeriod.id === fiscalPeriod.id) {
                            // Using setTimeout to ensure the editor is fully rendered
                            setTimeout(() => {
                              e.component.focus();
                            }, 600);
                          }
                        },
                      }}
                    ></SimpleItem>
                  ))
              : null}
          </GroupItem>

          <GroupItem
            colSpan={2}
            cssClass="buttons-group"
            colCountByScreen={
              hasCalculationFormula
                ? {
                    xs: 1,
                    sm: 1,
                    md: 1,
                    lg: 1,
                  }
                : {
                    xs: 2,
                    sm: 2,
                    md: 2,
                    lg: 2,
                  }
            }
          >
            {hasCalculationFormula ? null : (
              <ButtonItem
                buttonOptions={{
                  text: "SUBMIT",
                  type: "default" as ButtonType,
                  useSubmitBehavior: true,
                  width: "120px",
                }}
              />
            )}

            <ButtonItem
              buttonOptions={{
                disabled: false,
                text: "CANCEL",
                width: "120px",
                onClick: handleCancel,
              }}
              name="Cancel"
            />
          </GroupItem>
        </Form>
      </form>
    </Popup>
  );
};

export default ReviewProductionIndicesEditForm;
